import { CombinedQueriesResult } from '@lifeomic/one-query';
import { RestAPIEndpoints } from '@lifeomic/react-client';
import { QueryObserverResult } from '@tanstack/react-query';
import { some } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiHooks } from '../../../../hooks/useRestAPIHooks';
import {
  CompositeReportDefinitionSummary,
  Folder,
  ModelUtils,
  rootFolder,
} from '../../../AITemplates/models';

export type TemplatingItemSummariesQueryResult =
  RestAPIEndpoints['GET /v1/ai-templates']['Response'];

export const useRecursiveCRDFetch = () => {
  const [crds, setCrds] = useState<CompositeReportDefinitionSummary[]>([]);
  const [folders, setFolders] = useState<Folder[]>([rootFolder]);
  const [prevFolders, setPrevFolders] = useState<Folder[]>([]);

  if (folders.length !== prevFolders.length) {
    setPrevFolders(folders);
  }

  const pushFolder = useCallback(
    (newFolder: Folder) => {
      if (!some(folders, { path: newFolder.path })) {
        setFolders(prevFolders => [...prevFolders, newFolder]);
      }
    },
    [folders],
  );

  const pushCrd = useCallback(
    (newCrd: CompositeReportDefinitionSummary) => {
      if (!some(crds, { durableId: newCrd.durableId })) {
        setCrds(prevCrds => [...prevCrds, newCrd]);
      }
    },
    [crds],
  );

  const select = useCallback(
    (queryResponse: TemplatingItemSummariesQueryResult) => {
      const summaries = queryResponse.items
        .filter(
          summary =>
            summary.type === 'folder' ||
            summary.type === 'compositeReportDefinition',
        )
        .map(ModelUtils.templatingItemSummaryFromRawData);
      return summaries;
    },
    [],
  );

  const queries = useMemo(
    () =>
      folders.map(folder => [
        'GET /v1/ai-templates' as const,
        { prefix: folder.path },
        {
          select,
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchOnMount: false,
        },
      ]),
    [folders, select],
  );

  const combinedQuery = apiHooks.useCombinedAPIQueries(
    // @ts-expect-error we can transform the data in the select function, react-client types don't support this
    ...queries,
  ) as CombinedQueriesResult<
    QueryObserverResult<(Folder | CompositeReportDefinitionSummary)[]>[]
  >;

  // update folders and crds if new summaries are added
  useEffect(() => {
    for (const summarries of combinedQuery.data ?? []) {
      for (const summary of summarries) {
        if (summary.type === 'folder') {
          pushFolder(summary);
        } else {
          pushCrd(summary);
        }
      }
    }
  }, [combinedQuery.data, pushFolder, pushCrd]);

  const isLoading = useMemo(() => {
    return (
      combinedQuery.queries.some(query => query.isLoading) ||
      folders.length !== prevFolders.length
    );
  }, [combinedQuery.queries, folders, prevFolders]);

  return {
    compositeReportDefinitions: crds,
    isLoading,
  };
};
